import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import LoadingSpinner from "../LoadingSpinner";
import {
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  BellIcon,
  EnvelopeIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";

const PrivacySettings = ({ showMessage, user }) => {
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    profile_visibility: user?.profile_visibility || "team",
    activity_visibility: user?.activity_visibility || true,
    email_visibility: user?.email_visibility || false,
    analytics_opt_in: user?.analytics_opt_in || true,
    notifications: {
      email_notifications: user?.notifications?.email_notifications ?? true,
      push_notifications: user?.notifications?.push_notifications ?? true,
      weekly_reports: user?.notifications?.weekly_reports ?? true,
      campaign_updates: user?.notifications?.campaign_updates ?? true,
      security_alerts: user?.notifications?.security_alerts ?? true,
      product_updates: user?.notifications?.product_updates ?? false,
      marketing_emails: user?.notifications?.marketing_emails ?? false,
    },
  });

  const profileVisibilityOptions = [
    {
      value: "public",
      label: "Public",
      description:
        "Your profile is visible to anyone with access to the platform",
      icon: EyeIcon,
    },
    {
      value: "team",
      label: "Team Only",
      description: "Only members of your organization can see your profile",
      icon: ShieldCheckIcon,
    },
    {
      value: "private",
      label: "Private",
      description: "Your profile is only visible to you",
      icon: EyeSlashIcon,
    },
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("notifications.")) {
      const notificationName = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          [notificationName]: checked,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await updateUser(formData);
      if (result.success) {
        showMessage("success", "Privacy settings updated successfully!");
      } else {
        showMessage(
          "error",
          result.error || "Failed to update privacy settings",
        );
      }
    } catch (error) {
      showMessage("error", "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Profile Visibility */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Profile Visibility
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Control who can see your profile information and professional details.
        </p>

        <div className="space-y-3">
          {profileVisibilityOptions.map((option) => (
            <div key={option.value} className="relative">
              <label className="flex items-start p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  type="radio"
                  name="profile_visibility"
                  value={option.value}
                  checked={formData.profile_visibility === option.value}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center">
                    <option.icon className="h-5 w-5 text-gray-400 mr-2" />
                    <span className="text-sm font-medium text-gray-900">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {option.description}
                  </p>
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Activity & Content Visibility */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Activity & Content
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-start">
              <ChartBarIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Activity Visibility
                </h4>
                <p className="text-sm text-gray-500">
                  Show your recent activities and engagement metrics to others
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="activity_visibility"
                checked={formData.activity_visibility}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-start">
              <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Email Visibility
                </h4>
                <p className="text-sm text-gray-500">
                  Allow team members to see your email address
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="email_visibility"
                checked={formData.email_visibility}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Analytics & Data */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Analytics & Data
        </h3>

        <div className="p-4 border border-gray-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-start">
              <ChartBarIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-gray-900">
                  Usage Analytics
                </h4>
                <p className="text-sm text-gray-500">
                  Help us improve by sharing anonymous usage data
                </p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="analytics_opt_in"
                checked={formData.analytics_opt_in}
                onChange={handleChange}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Communication Preferences
        </h3>

        <div className="space-y-4">
          {/* Essential Notifications */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Essential Notifications
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    Email Notifications
                  </h5>
                  <p className="text-xs text-gray-500">
                    Account updates and important alerts
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="notifications.email_notifications"
                  checked={formData.notifications.email_notifications}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    Security Alerts
                  </h5>
                  <p className="text-xs text-gray-500">
                    Login attempts and security events
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="notifications.security_alerts"
                  checked={formData.notifications.security_alerts}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>
            </div>
          </div>

          {/* Activity Notifications */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Activity Notifications
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    Campaign Updates
                  </h5>
                  <p className="text-xs text-gray-500">
                    Campaign performance and milestone alerts
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="notifications.campaign_updates"
                  checked={formData.notifications.campaign_updates}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    Weekly Reports
                  </h5>
                  <p className="text-xs text-gray-500">
                    Weekly performance summaries
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="notifications.weekly_reports"
                  checked={formData.notifications.weekly_reports}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    Push Notifications
                  </h5>
                  <p className="text-xs text-gray-500">
                    Browser notifications for real-time updates
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="notifications.push_notifications"
                  checked={formData.notifications.push_notifications}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>
            </div>
          </div>

          {/* Marketing & Updates */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Marketing & Updates
            </h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    Product Updates
                  </h5>
                  <p className="text-xs text-gray-500">
                    New features and platform improvements
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="notifications.product_updates"
                  checked={formData.notifications.product_updates}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>

              <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    Marketing Emails
                  </h5>
                  <p className="text-xs text-gray-500">
                    Tips, best practices, and promotional content
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="notifications.marketing_emails"
                  checked={formData.notifications.marketing_emails}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={isLoading}
          className="bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-lg shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isLoading ? (
            <>
              <LoadingSpinner size="sm" />
              <span>Saving...</span>
            </>
          ) : (
            <span>Save Changes</span>
          )}
        </button>
      </div>
    </form>
  );
};

export default PrivacySettings;
